import Form from '@components/Form'
import { type SavedPrimitive, useSavePrimitive } from '@features/fields/api/useSavePrimitives'
import { GuidancePrimitive } from 'api-validations/locutus/guidance'
import { useEffect, useRef, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { ButtonKind, CircleSpinner, PanoramaButton, UndoIcon } from 'shared-ui/components'
import { MeasureSystem, useLocalization } from 'shared-ui/localization'
import {
  clearActiveGuidanceLine,
  DrawMode,
  DrawStyle,
  useActiveGuidanceLine,
  useMapContext
} from 'shared-ui/map'
import { convertFromImperial, convertToImperial, isNonNil } from 'utils'
import { z } from 'zod'

import { BottomButtons } from '../BoundaryOptions'
import { GuidanceTools } from '../ReusedComponents'

type Zone = { id: string; name: string; color: string }

const PLACEHOLDER_POINT: [number, number] = [0, 0]

const isPlaceholderPoint = ([lng, lat]: [number, number]) =>
  lng === PLACEHOLDER_POINT[0] && lat === PLACEHOLDER_POINT[1]

const TrailingUnit = ({ value, unit }: { value: string; unit: string }) => (
  <span className="pointer-events-none absolute inset-y-0 left-px flex items-center px-2">
    <span className="invisible whitespace-pre">{value === '' ? '0' : value}</span>
    <span className="ml-1 text-sm text-gray-400">{unit}</span>
  </span>
)

type FormValues = {
  name: string
  implementWidth: number
  implementWidthUnit: 'ft' | 'm'
  rowSpacing: number
  rowSpacingUnit: 'in' | 'cm'
  rows: number
  lineType: GuidancePrimitive
  pointALat: string
  pointALng: string
  pointBLat: string
  pointBLng: string
  heading: string
  radius: string
}

type Props = {
  zone?: Zone
  // Suggested name prefilled into the (editable) name field. Assets are
  // immutable, so the user always sees the name that will be persisted.
  defaultName?: string
  onSave: (name: string) => void
  onBack: () => void
  isPending?: boolean
  saveError?: string
  onPointsChange?: (pointA: [number, number], pointB: [number, number]) => void
  onAnchorChange?: (pointA: [number, number]) => void
  onHeadingChange?: (heading: number) => void
  onRadiusChange?: (radiusFt: number) => void
  onLineTypeChange?: (type: GuidancePrimitive) => void
  onUndo?: () => void
  activeGuidanceLine?: {
    id: string
    pointA: [number, number]
    pointB: [number, number]
    radiusFt?: number
  }
  // Set once any guidance line has been drawn in this session. Distinct from
  // `activeGuidanceLine` because Curve primitives don't emit an activeGuidanceLine
  // (they have no meaningful pointA/pointB), but we still need to know a line
  // exists so the Save button can enable.
  hasDrawnLine: boolean
  // True while helm's live propagation preview for the currently drawn line
  // is in flight — a change to the sketch just kicked off a helm/path fetch
  // and the map indicator hasn't caught up yet.
  previewLoading?: boolean
}

function bearingDeg(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const toRad = (d: number) => (d * Math.PI) / 180
  const φ1 = toRad(lat1)
  const φ2 = toRad(lat2)
  const Δλ = toRad(lng2 - lng1)
  const y = Math.sin(Δλ) * Math.cos(φ2)
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ)
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360
}

export const NewGuidanceLine = ({
  zone,
  defaultName,
  onBack,
  onSave,
  isPending,
  saveError,
  onPointsChange,
  onAnchorChange,
  onHeadingChange,
  onRadiusChange,
  onLineTypeChange,
  onUndo,
  activeGuidanceLine,
  hasDrawnLine,
  previewLoading = false
}: Props) => {
  const { t, measureSystem } = useLocalization()
  const isMetric = measureSystem === MeasureSystem.Metric

  const { register, control, setValue, getValues } = useForm<FormValues>({
    defaultValues: {
      name: defaultName ?? '',
      implementWidth: isMetric ? Math.round(convertFromImperial(40, 'ft')) : 40,
      implementWidthUnit: isMetric ? 'm' : 'ft',
      rowSpacing: isMetric ? Math.round(convertFromImperial(30, 'in')) : 30,
      rowSpacingUnit: isMetric ? 'cm' : 'in',
      rows: 16,
      lineType: GuidancePrimitive.ABLine,
      pointALat: '',
      pointALng: '',
      pointBLat: '',
      pointBLng: '',
      heading: '',
      radius: ''
    }
  })

  const [
    pointALat,
    pointALng,
    pointBLat,
    pointBLng,
    headingWatch,
    lineTypeWatch,
    radiusWatch,
    nameWatch
  ] = useWatch({
    control,
    name: [
      'pointALat',
      'pointALng',
      'pointBLat',
      'pointBLng',
      'heading',
      'lineType',
      'radius',
      'name'
    ]
  })

  // Debounce user input so a single keystroke doesn't round-trip helm
  const INPUT_DEBOUNCE_MS = 250

  const ranges = {
    lat: {
      schema: z.number().min(-90).max(90),
      label: t(($) => $.planning.latitude),
      min: -90,
      max: 90
    },
    lng: {
      schema: z.number().min(-180).max(180),
      label: t(($) => $.planning.longitude),
      min: -180,
      max: 180
    },
    heading: {
      schema: z.number().min(0).max(360),
      label: t(($) => $.planning.heading),
      min: 0,
      max: 360
    },
    radius: {
      schema: z.number().min(0).max(1500),
      label: t(($) => $.planning.radius),
      min: 0,
      max: 1500
    }
  } as const
  type Range = (typeof ranges)[keyof typeof ranges]
  const isValidLat = (v: number) => ranges.lat.schema.safeParse(v).success
  const isValidLng = (v: number) => ranges.lng.schema.safeParse(v).success
  const isValidHeading = (v: number) => ranges.heading.schema.safeParse(v).success

  // Blank / not-yet-parseable input isn't an error — the user is still typing.
  // Only surface a message once the value parses to a finite number that
  // sits outside the accepted range.
  const rangeError = (raw: string, range: Range): string | undefined => {
    if (raw === '') return undefined
    const v = parseFloat(raw)
    if (!Number.isFinite(v)) return undefined
    return range.schema.safeParse(v).success
      ? undefined
      : t(($) => $.planning.rangeError, {
          label: range.label,
          min: range.min,
          max: range.max
        })
  }
  const pointALatError = rangeError(pointALat, ranges.lat)
  const pointALngError = rangeError(pointALng, ranges.lng)
  const pointBLatError = rangeError(pointBLat, ranges.lat)
  const pointBLngError = rangeError(pointBLng, ranges.lng)
  const headingError = rangeError(headingWatch, ranges.heading)
  const radiusError = rangeError(radiusWatch, ranges.radius)

  // ABLine only: auto-compute heading from A/B and push both points to the
  // map. AHeading/RadialPivot handle Point A separately (see anchor effect
  // below) so editing A doesn't recompute the heading — that value stays
  // as the user typed it and B slides to match.
  useEffect(() => {
    if (lineTypeWatch !== GuidancePrimitive.ABLine) return
    const aLat = parseFloat(pointALat),
      aLng = parseFloat(pointALng)
    const bLat = parseFloat(pointBLat),
      bLng = parseFloat(pointBLng)
    if (!isValidLat(aLat) || !isValidLng(aLng) || !isValidLat(bLat) || !isValidLng(bLng)) return
    const timeout = setTimeout(() => {
      const computed = bearingDeg(aLat, aLng, bLat, bLng)
      const current = parseFloat(getValues('heading'))
      if (isNaN(current) || Math.abs(current - computed) > 0.01) {
        setValue('heading', computed.toFixed(2))
      }
      onPointsChange?.([aLng, aLat], [bLng, bLat])
    }, INPUT_DEBOUNCE_MS)
    return () => clearTimeout(timeout)
  }, [pointALat, pointALng, pointBLat, pointBLng, lineTypeWatch]) // eslint-disable-line react-hooks/exhaustive-deps

  // AHeading/RadialPivot: Point A is user-editable but B is hidden and
  // derived from heading/radius. Move A on the map while preserving the
  // heading (AHeading) or radius+bearing (RadialPivot) so the input value
  // stays put and the hidden handle slides to match.
  useEffect(() => {
    if (
      lineTypeWatch !== GuidancePrimitive.AHeading &&
      lineTypeWatch !== GuidancePrimitive.RadialPivot
    ) {
      return
    }
    const aLat = parseFloat(pointALat)
    const aLng = parseFloat(pointALng)
    if (!isValidLat(aLat) || !isValidLng(aLng)) return
    const timeout = setTimeout(() => onAnchorChange?.([aLng, aLat]), INPUT_DEBOUNCE_MS)
    return () => clearTimeout(timeout)
  }, [pointALat, pointALng, lineTypeWatch]) // eslint-disable-line react-hooks/exhaustive-deps

  // When the user manually edits the heading, rotate point B around point A.
  useEffect(() => {
    const aLat = parseFloat(pointALat),
      aLng = parseFloat(pointALng)
    const h = parseFloat(headingWatch)
    if (!isValidHeading(h) || !isValidLat(aLat) || !isValidLng(aLng)) return
    const timeout = setTimeout(() => onHeadingChange?.(h), INPUT_DEBOUNCE_MS)
    return () => clearTimeout(timeout)
  }, [headingWatch]) // eslint-disable-line react-hooks/exhaustive-deps

  // Populate form when the map places or updates the guidance line.
  // Epsilon guard prevents feeding the update back when the form itself triggered setGuidanceLine.
  useEffect(() => {
    if (!activeGuidanceLine) return
    if (isPlaceholderPoint(activeGuidanceLine.pointA)) return
    const eq = (formVal: string, coord: number) => Math.abs(parseFloat(formVal) - coord) < 1e-6
    const coordsUnchanged =
      eq(getValues('pointALat'), activeGuidanceLine.pointA[1]) &&
      eq(getValues('pointALng'), activeGuidanceLine.pointA[0]) &&
      eq(getValues('pointBLat'), activeGuidanceLine.pointB[1]) &&
      eq(getValues('pointBLng'), activeGuidanceLine.pointB[0])
    if (!coordsUnchanged) {
      setValue('pointALat', activeGuidanceLine.pointA[1].toFixed(8))
      setValue('pointALng', activeGuidanceLine.pointA[0].toFixed(8))
      setValue('pointBLat', activeGuidanceLine.pointB[1].toFixed(8))
      setValue('pointBLng', activeGuidanceLine.pointB[0].toFixed(8))
    }
    const [aLng, aLat] = activeGuidanceLine.pointA
    const [bLng, bLat] = activeGuidanceLine.pointB
    // The seed placeholder lands at A === B until the user picks a direction,
    // which would make bearingDeg return 0 (or NaN) and clobber a heading the
    // user already typed. Only sync once the two points differ.
    const hasDirection = aLng !== bLng || aLat !== bLat
    const currentLineType = getValues('lineType')
    if (
      hasDirection &&
      (currentLineType === GuidancePrimitive.AHeading ||
        currentLineType === GuidancePrimitive.RadialPivot)
    ) {
      const heading = bearingDeg(aLat, aLng, bLat, bLng)
      const currentHeading = parseFloat(getValues('heading'))
      if (isNaN(currentHeading) || Math.abs(currentHeading - heading) > 0.01) {
        setValue('heading', heading.toFixed(2))
      }
    }
    if (activeGuidanceLine.radiusFt !== undefined) {
      const displayRadius = isMetric
        ? convertFromImperial(activeGuidanceLine.radiusFt, 'ft')
        : activeGuidanceLine.radiusFt
      const currentRadius = parseFloat(getValues('radius'))
      if (isNaN(currentRadius) || Math.abs(currentRadius - displayRadius) > 0.1) {
        setValue('radius', displayRadius.toFixed(2))
      }
    }
  }, [activeGuidanceLine]) // eslint-disable-line react-hooks/exhaustive-deps

  // When the user manually edits the radius, update the map line length.
  useEffect(() => {
    const r = parseFloat(radiusWatch)
    // Larger than the biggest real center-pivot systems (~2500 ft / 750 m);
    // anything past this is either a typo or a half-typed value, and pushing
    // it to helm blows out the clipping / propagation arithmetic.
    if (!Number.isFinite(r) || r <= 0 || r > 5000) return
    const radiusFt = isMetric ? convertToImperial(r, 'm') : r
    const timeout = setTimeout(() => onRadiusChange?.(radiusFt), INPUT_DEBOUNCE_MS)
    return () => clearTimeout(timeout)
  }, [radiusWatch]) // eslint-disable-line react-hooks/exhaustive-deps

  // Swap the seed feature to the newly-picked primitive type so the map
  // representation and helm request follow the form's selection. Skip the
  // initial render so we don't churn the freshly-added feature.
  const lineTypeInitialRef = useRef(true)
  useEffect(() => {
    if (lineTypeInitialRef.current) {
      lineTypeInitialRef.current = false
      return
    }
    onLineTypeChange?.(lineTypeWatch)
  }, [lineTypeWatch]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div className="flex flex-col gap-4 py-4">
        {isNonNil(zone) && (
          <div className="mt-0.5 flex items-center gap-1.5">
            <span
              className="size-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: zone.color }}
            />
            <span className="text-sm text-gray-500">{zone.name}</span>
          </div>
        )}

        <p className="text-sm text-gray-500">{t(($) => $.planning.newGuidanceIntro)}</p>

        <label className="flex flex-col gap-1">
          <span className="text-sm font-semibold text-gray-900">{t(($) => $.labels.name)}</span>
          <Form.Input
            {...register('name')}
            placeholder={t(($) => $.labels.name)}
            className="rounded-xl border border-gray-200"
          />
        </label>

        <div className="flex flex-col gap-2">
          <span className="text-sm font-semibold text-gray-900">
            {t(($) => $.planning.lineType)}
          </span>
          <Controller
            control={control}
            name="lineType"
            render={({ field }) => <GuidanceTools onChange={field.onChange} />}
          />
        </div>

        {lineTypeWatch !== GuidancePrimitive.Curve && (
          <>
            {' '}
            <div className="flex flex-col gap-1">
              <h3 className="text-xl font-bold text-gray-900">{t(($) => $.planning.points)}</h3>
              <p className="text-sm text-gray-500">{t(($) => $.planning.pointsInstruction)}</p>
            </div>
            <div className="flex flex-col gap-3">
              <p className="text-sm font-bold text-gray-900">{t(($) => $.planning.pointA)}</p>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex flex-col gap-1">
                  <span className="text-sm text-gray-500">{t(($) => $.planning.latitude)}</span>
                  <Form.Input
                    {...register('pointALat')}
                    type="number"
                    step="any"
                    min={-90}
                    max={90}
                    onWheel={(e) => e.currentTarget.blur()}
                    placeholder="0.00000000"
                    hasError={pointALatError !== undefined}
                    className="rounded-xl border border-gray-200"
                  />
                  {pointALatError !== undefined && (
                    <span className="text-xs text-red-600">{pointALatError}</span>
                  )}
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-sm text-gray-500">{t(($) => $.planning.longitude)}</span>
                  <Form.Input
                    {...register('pointALng')}
                    type="number"
                    step="any"
                    min={-180}
                    max={180}
                    onWheel={(e) => e.currentTarget.blur()}
                    placeholder="0.00000000"
                    hasError={pointALngError !== undefined}
                    className="rounded-xl border border-gray-200"
                  />
                  {pointALngError !== undefined && (
                    <span className="text-xs text-red-600">{pointALngError}</span>
                  )}
                </label>
              </div>
            </div>
            {lineTypeWatch === GuidancePrimitive.ABLine && (
              <div className="flex flex-col gap-3">
                <p className="text-sm font-bold text-gray-900">{t(($) => $.planning.pointB)}</p>
                <div className="grid grid-cols-2 gap-3">
                  <label className="flex flex-col gap-1">
                    <span className="text-sm text-gray-500">{t(($) => $.planning.latitude)}</span>
                    <Form.Input
                      {...register('pointBLat')}
                      type="number"
                      step="any"
                      min={-90}
                      max={90}
                      onWheel={(e) => e.currentTarget.blur()}
                      placeholder="0.00000000"
                      hasError={pointBLatError !== undefined}
                      className="rounded-xl border border-gray-200"
                    />
                    {pointBLatError !== undefined && (
                      <span className="text-xs text-red-600">{pointBLatError}</span>
                    )}
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-sm text-gray-500">{t(($) => $.planning.longitude)}</span>
                    <Form.Input
                      {...register('pointBLng')}
                      type="number"
                      step="any"
                      min={-180}
                      max={180}
                      onWheel={(e) => e.currentTarget.blur()}
                      placeholder="0.00000000"
                      hasError={pointBLngError !== undefined}
                      className="rounded-xl border border-gray-200"
                    />
                    {pointBLngError !== undefined && (
                      <span className="text-xs text-red-600">{pointBLngError}</span>
                    )}
                  </label>
                </div>
              </div>
            )}
            {lineTypeWatch === GuidancePrimitive.AHeading && (
              <label className="flex flex-col gap-1">
                <span className="text-sm font-semibold text-gray-900">
                  {t(($) => $.planning.heading)}
                </span>
                <div className="relative mb-4">
                  <Form.Input
                    {...register('heading')}
                    type="number"
                    step="any"
                    min={0}
                    max={360}
                    onWheel={(e) => e.currentTarget.blur()}
                    placeholder="0"
                    hasError={headingError !== undefined}
                    className="mb-0 w-full rounded-xl border border-gray-200"
                  />
                  <TrailingUnit value={headingWatch} unit="°" />
                </div>
                {headingError !== undefined && (
                  <span className="text-xs text-red-600">{headingError}</span>
                )}
              </label>
            )}
            {lineTypeWatch === GuidancePrimitive.RadialPivot && (
              <label className="flex flex-col gap-1">
                <span className="text-sm font-semibold text-gray-900">
                  {t(($) => $.planning.radius)}
                </span>
                <div className="relative mb-4">
                  <Form.Input
                    {...register('radius')}
                    type="number"
                    step="any"
                    min={0}
                    max={1500}
                    onWheel={(e) => e.currentTarget.blur()}
                    placeholder="0"
                    hasError={radiusError !== undefined}
                    className="mb-0 w-full rounded-xl border border-gray-200"
                  />
                  <TrailingUnit value={radiusWatch} unit={isMetric ? 'm' : 'ft'} />
                </div>
                {radiusError !== undefined && (
                  <span className="text-xs text-red-600">{radiusError}</span>
                )}
              </label>
            )}
          </>
        )}
      </div>
      <div className="flex items-center justify-between pb-2">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          {previewLoading && (
            <>
              <CircleSpinner size="size-4" />
              <span>{t(($) => $.planning.lineList.previewLoading)}</span>
            </>
          )}
        </div>
        {onUndo !== undefined && (
          <PanoramaButton
            type="button"
            kind={ButtonKind.Secondary}
            className="h-fit py-1"
            onClick={onUndo}
          >
            {t(($) => $.buttons.undo)}
            <UndoIcon />
          </PanoramaButton>
        )}
      </div>
      <BottomButtons
        onCancel={onBack}
        onSave={() => onSave(getValues('name'))}
        saveDisabledWhen={[
          {
            when: !hasDrawnLine,
            reason: t(($) => $.disabledReasons.fillInRequiredFields)
          },
          {
            when: nameWatch.trim().length === 0,
            reason: t(($) => $.disabledReasons.enterNameFirst)
          }
        ]}
        savePending={isPending}
        saveError={saveError}
      />
    </>
  )
}

export const GuidanceLineDrawing = ({
  onBack,
  onSaved,
  fieldId,
  zone,
  defaultName,
  drawZoneId,
  previewLoading
}: {
  fieldId: string
  onBack: () => void
  onSaved?: (saved: SavedPrimitive) => void
  zone?: Zone
  defaultName?: string
  // Zone tag applied to the auto-placed sketch so the parent's Back cleanup
  // (which filters by zone) removes it when the user cancels without saving.
  drawZoneId: string
  previewLoading?: boolean
}) => {
  const { t } = useLocalization()
  const { map } = useMapContext()
  const activeLine = useActiveGuidanceLine()
  // Keep a ref so inline callbacks always see the latest value without re-subscribing.
  const activeLineRef = useRef(activeLine)
  useEffect(() => {
    activeLineRef.current = activeLine
  }, [activeLine])

  // Curve primitives don't emit an `activeGuidanceLine` (they have no A/B).
  // Track the drawn feature id from `onDrawEnd` (multi-listener, so it
  // coexists with the parent's `onChange` helm trigger) and clear it on
  // `onDrawAbort`, or when Undo removes the feature, so Save never stays
  // enabled for a line that isn't on the map.
  // Seeded with a fresh uuid so the mount-time auto-place has a stable id
  // without a cascading setState from inside the mount effect.
  const [drawnFeatureId, setDrawnFeatureId] = useState<string | undefined>(() =>
    crypto.randomUUID()
  )
  const drawnFeatureIdRef = useRef<string | undefined>(drawnFeatureId)
  useEffect(() => {
    drawnFeatureIdRef.current = drawnFeatureId
  }, [drawnFeatureId])
  const [userDrew, setUserDrew] = useState(false)
  useEffect(() => {
    const drawEnd = map.drawTool.onDrawEnd((feature) => {
      const id = feature.id
      const strId = id === undefined ? undefined : String(id)
      drawnFeatureIdRef.current = strId
      setDrawnFeatureId(strId)
      setUserDrew(true)
    })
    const drawAbort = map.drawTool.onDrawAbort(() => {
      drawnFeatureIdRef.current = undefined
      setDrawnFeatureId(undefined)
    })
    return () => {
      drawEnd.cleanup()
      drawAbort.cleanup()
    }
  }, [map.drawTool])

  useEffect(() => {
    map.drawTool.setDrawStyle({ mode: DrawMode.ABLine, shape: DrawStyle.Line })

    // Seed a placeholder AB line at (0, 0) so typed coordinates have a line to
    // move, no drawing required.
    const id = drawnFeatureIdRef.current
    if (id !== undefined) {
      map.drawTool.addGuidancePrimitive(
        {
          type: 'Feature',
          id,
          geometry: {
            type: 'LineString',
            coordinates: [PLACEHOLDER_POINT, PLACEHOLDER_POINT]
          },
          properties: {}
        },
        GuidancePrimitive.ABLine,
        drawZoneId
      )
    }
    map.drawTool.clearHistory()

    return () => {
      map.drawTool.disable()
      clearActiveGuidanceLine()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const seedMoved = isNonNil(activeLine) && !isPlaceholderPoint(activeLine.pointA)
  const hasDrawnLine = drawnFeatureId !== undefined && (userDrew || seedMoved)

  const save = useSavePrimitive(fieldId, (results) => {
    const saved = results.at(0)
    if (saved !== undefined) onSaved?.(saved)
  })

  return (
    // Any pointer-down on the drawing pane behaves like double-clicking the
    // last vertex: an in-progress curve commits as a finished sketch instead
    // of being aborted when the user moves off the map to adjust inputs or
    // press Back. `finishDrawing` is a no-op when nothing is being drawn.
    <div
      className="contents"
      onPointerDown={() => {
        map.drawTool.finishDrawing()
      }}
    >
      <NewGuidanceLine
        zone={zone}
        defaultName={defaultName}
        onBack={onBack}
        hasDrawnLine={hasDrawnLine}
        onSave={(name) => {
          // A freshly drawn Curve gets a new feature id from `onDrawEnd`, but
          // `activeGuidanceLine` doesn't refresh for Curve (it only carries
          // A/B/radius, which curves don't have), so it still points at the
          // seed placeholder. Prefer the drawn id — for AB/Heading/Pivot the
          // two ids agree, so this stays correct for those paths too.
          const id = drawnFeatureIdRef.current ?? activeLineRef.current?.id
          if (id === undefined) return
          save.mutate([{ featId: id, name }])
        }}
        isPending={save.isPending}
        saveError={
          save.isError ? (save.error?.message ?? t(($) => $.messages.errorOccurred)) : undefined
        }
        activeGuidanceLine={activeLine}
        onPointsChange={(pointA, pointB) => {
          const id = activeLineRef.current?.id
          if (isNonNil(id)) map.drawTool.updateGuidanceLinePoints(id, pointA, pointB)
        }}
        onAnchorChange={(pointA) => {
          const id = activeLineRef.current?.id
          if (isNonNil(id)) map.drawTool.updateGuidanceLineAnchor(id, pointA)
        }}
        onHeadingChange={(heading) => {
          const id = activeLineRef.current?.id
          if (isNonNil(id)) map.drawTool.updateGuidanceLineHeading(id, heading)
        }}
        onRadiusChange={(radiusFt) => {
          const id = activeLineRef.current?.id
          if (isNonNil(id)) map.drawTool.updateGuidanceLineRadius(id, radiusFt)
        }}
        onLineTypeChange={(type) => {
          const id = activeLineRef.current?.id ?? drawnFeatureIdRef.current
          if (isNonNil(id)) map.drawTool.convertGuidanceLineType(id, type)
        }}
        onUndo={() => {
          map.drawTool.undo()
          const id = drawnFeatureIdRef.current
          const stillDrawn = map.drawTool
            .getFeatureCollection()
            .features.some((feature) => String(feature.id) === id)
          if (!stillDrawn) setDrawnFeatureId(undefined)
        }}
        previewLoading={previewLoading}
      />
    </div>
  )
}
